import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Search,
  Download,
  MessageCircle,
  MapPin,
  Phone,
  Calendar,
  Sprout,
  RefreshCw,
  AlertCircle,
  Filter,
  ExternalLink,
  Package,
  Check,
  Plane,
  Wrench,
  Cpu,
  Settings,
  Star,
  HelpCircle,
  Image as ImageIcon,
  Sparkles,
} from "lucide-react";
import {
  SiteContentPanel,
  ServicesPanel,
  PortfolioPanel,
  TestimonialsPanel,
  FaqPanel,
  ProductsFullPanel,
  BundlesPanel,
} from "./admin/ContentPanels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "skma_admin_token";

const rupiah = (n) =>
  n
    ? new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(Math.round(n))
    : "-";

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const STOCK_OPTIONS = [
  { id: "ready", label: "Ready Stock", cls: "bg-emerald-500" },
  { id: "preorder", label: "Pre-Order", cls: "bg-amber-500" },
  { id: "soldout", label: "Sold Out", cls: "bg-slate-800" },
];

const CATEGORY_LABEL = { drone: "Drone", sparepart: "Sparepart", aksesoris: "Aksesoris" };


// ==== Login ====
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/verify`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-900 flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-3xl p-8 sm:p-10 shadow-2xl"
      >
        <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/30 mx-auto">
          <Lock className="w-7 h-7 text-white" />
        </div>
        <h1 className="mt-6 text-2xl font-extrabold text-slate-900 text-center">
          Admin SKMA
        </h1>
        <p className="mt-1 text-sm text-slate-500 text-center">
          Masukkan password untuk akses booking monitoring
        </p>

        <div className="mt-8">
          <label className="block text-sm font-semibold text-slate-900 mb-2">
            Password Admin
          </label>
          <input
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="••••••••••"
          />
        </div>

        {err && (
          <div
            data-testid="admin-login-error"
            className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors"
        >
          {loading ? "Memverifikasi..." : "Masuk"}
        </button>

        <div className="mt-6 text-center">
          <a href="/" className="text-xs text-slate-500 hover:text-emerald-700">
            ← Kembali ke landing page
          </a>
        </div>
      </form>
    </div>
  );
};

// ==== Dashboard ====
const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, total_hectares: 0 });

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServicesList(r.data || [])).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({
    service: "all",
    status: "all",
    from_date: "",
    to_date: "",
    q: "",
  });

  const load = useCallback(async (activeFilters) => {
    const f = activeFilters || filters;
    setLoading(true);
    setErr("");
    try {
      const headers = { "X-Admin-Token": token };
      const params = {};
      if (f.service && f.service !== "all") params.service = f.service;
      if (f.status && f.status !== "all") params.status = f.status;
      if (f.from_date) params.from_date = f.from_date;
      if (f.to_date) params.to_date = f.to_date;
      if (f.q) params.q = f.q;

      const [bRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setBookings(bRes.data);
      setStats(sRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
        return;
      }
      setErr("Gagal load data. Coba refresh.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, onLogout]);

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const exportCsv = () => {
    const headers = [
      "Tanggal",
      "Nama",
      "WhatsApp",
      "Lokasi",
      "Layanan",
      "Komoditas",
      "Luas (Ha)",
      "Tanggal Target",
      "Estimasi",
      "Catatan",
    ];
    const rows = bookings.map((b) => [
      fmtDate(b.created_at),
      b.name,
      b.phone,
      b.location,
      b.service,
      b.commodity || "",
      b.hectares,
      b.date || "",
      b.estimated_price || "",
      (b.notes || "").replace(/\n/g, " "),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `skma-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Topbar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-extrabold text-slate-900">Admin SKMA</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">Booking Monitor</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 text-sm font-medium px-3 py-2"
            >
              <ExternalLink className="w-4 h-4" /> Landing
            </a>
            <button
              onClick={() => load(filters)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 text-sm font-semibold px-3 py-2 rounded-full transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs">
          {[
            { id: "bookings", label: "Booking", icon: Calendar },
            { id: "products", label: "Produk & Stok", icon: Package },
            { id: "bundles", label: "Paket Bundle", icon: Sparkles },
            { id: "site", label: "Site Content", icon: Settings },
            { id: "services", label: "Layanan", icon: Sprout },
            { id: "portfolio", label: "Portofolio", icon: ImageIcon },
            { id: "testimonials", label: "Testimoni", icon: Star },
            { id: "faqs", label: "FAQ", icon: HelpCircle },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                  active
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-600/25"
                    : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-700"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "products" && <ProductsFullPanel token={token} onLogout={onLogout} />}
        {tab === "bundles" && <BundlesPanel token={token} onLogout={onLogout} />}
        {tab === "site" && <SiteContentPanel token={token} onLogout={onLogout} />}
        {tab === "services" && <ServicesPanel token={token} onLogout={onLogout} />}
        {tab === "portfolio" && <PortfolioPanel token={token} onLogout={onLogout} />}
        {tab === "testimonials" && <TestimonialsPanel token={token} onLogout={onLogout} />}
        {tab === "faqs" && <FaqPanel token={token} onLogout={onLogout} />}

        {tab === "bookings" && (<>
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Booking" value={stats.total} data-testid="stat-total" />
          <StatCard label="Booking Hari Ini" value={stats.today} accent="amber" data-testid="stat-today" />
          <StatCard label="Total Luas" value={`${Math.round(stats.total_hectares).toLocaleString("id-ID")} ha`} accent="emerald" data-testid="stat-hectares" />
          <StatCard label="Ditampilkan" value={bookings.length} accent="slate" data-testid="stat-filtered" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center gap-2 mb-4 text-slate-900 font-semibold">
            <Filter className="w-4 h-4 text-emerald-700" /> Filter
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Cari (nama/WA/lokasi)</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  data-testid="admin-search-input"
                  placeholder="Ketik & tekan Terapkan"
                  className="w-full pl-9 border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Layanan</label>
              <select
                value={filters.service}
                onChange={(e) => setFilters({ ...filters, service: e.target.value })}
                data-testid="admin-filter-service"
                className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              >
                <option value="all">Semua</option>
                {servicesList.map((s) => (
                  <option key={s.id} value={s.label}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                data-testid="admin-filter-status"
                className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              >
                <option value="all">Semua</option>
                {BOOKING_STATUSES.map((s) => (
                  <option key={s} value={s}>{BOOKING_STATUS_META[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Dari Tanggal</label>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                data-testid="admin-filter-from"
                className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sampai</label>
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                data-testid="admin-filter-to"
                className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
            <button
              onClick={() => {
                const empty = { service: "all", status: "all", from_date: "", to_date: "", q: "" };
                setFilters(empty);
                load(empty);
              }}
              className="text-sm text-slate-600 hover:text-emerald-700 font-medium"
              data-testid="admin-filter-reset"
            >
              Reset filter
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={exportCsv}
                data-testid="admin-export-csv"
                className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button
                onClick={() => load(filters)}
                data-testid="admin-filter-apply"
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors"
              >
                Terapkan
              </button>
            </div>
          </div>
        </div>

        {err && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {err}
          </div>
        )}

        {/* Bookings list */}
        {loading ? (
          <div className="text-center py-16 text-slate-500">Memuat data...</div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500" data-testid="admin-empty">
            <Sprout className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="font-semibold">Belum ada booking sesuai filter.</div>
            <div className="text-sm mt-1">Coba ubah filter atau tunggu submission masuk.</div>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="admin-booking-list">
            {bookings.map((b) => (
              <BookingRow
                key={b.id}
                b={b}
                token={token}
                onLogout={onLogout}
                onUpdated={(updated) =>
                  setBookings((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                }
              />
            ))}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent = "emerald", ...rest }) => {
  const map = {
    emerald: "text-emerald-700",
    amber: "text-amber-600",
    slate: "text-slate-800",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" {...rest}>
      <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className={`mt-2 text-3xl font-extrabold ${map[accent]}`}>{value}</div>
    </div>
  );
};

const BOOKING_STATUS_META = {
  new: { label: "Baru", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed: { label: "Konfirmasi", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  done: { label: "Selesai", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Dibatalkan", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const BOOKING_STATUSES = ["new", "confirmed", "done", "cancelled"];

const BookingRow = ({ b, token, onUpdated, onLogout }) => {
  const phoneClean = String(b.phone || "").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${phoneClean.startsWith("0") ? "62" + phoneClean.slice(1) : phoneClean}?text=${encodeURIComponent(`Halo ${b.name}, ini tim SKMA menindaklanjuti booking Anda untuk ${b.service} di ${b.location}.`)}`;
  const [saving, setSaving] = useState(false);
  const status = b.status || "new";

  const changeStatus = async (newStatus) => {
    if (newStatus === status) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/bookings/${b.id}`,
        { status: newStatus },
        { headers: { "X-Admin-Token": token } }
      );
      onUpdated && onUpdated(data);
    } catch (e) {
      if (e.response?.status === 401) return onLogout && onLogout();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`admin-booking-${b.id}`}
      className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 hover:border-emerald-500 hover:shadow-lg transition-all"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold text-slate-900">{b.name}</h3>
            <span
              data-testid={`booking-status-${b.id}`}
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${BOOKING_STATUS_META[status].cls}`}
            >
              {BOOKING_STATUS_META[status].label}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold uppercase tracking-wider">
              {b.service}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="truncate">{b.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="truncate">{b.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Sprout className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>{b.hectares} ha · {b.commodity || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>Target: {b.date || "-"}</span>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="text-slate-500">Dibuat: {fmtDate(b.created_at)}</span>
            </div>
          </div>
          {b.notes && (
            <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm text-slate-700">
              <span className="font-semibold text-slate-900">Catatan: </span>{b.notes}
            </div>
          )}
        </div>
        <div className="flex sm:flex-col items-stretch gap-2 sm:w-44 flex-shrink-0">
          {b.estimated_price && (
            <div className="flex-1 sm:flex-none bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Estimasi</div>
              <div className="text-emerald-800 font-extrabold text-sm">{rupiah(b.estimated_price)}</div>
            </div>
          )}
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`admin-wa-${b.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Balas WA
          </a>
          <div className="flex flex-wrap gap-1 mt-1" data-testid={`booking-status-controls-${b.id}`}>
            {BOOKING_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => changeStatus(s)}
                disabled={saving}
                data-testid={`booking-status-btn-${b.id}-${s}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border transition-all ${
                  status === s
                    ? `${BOOKING_STATUS_META[s].cls} border-current`
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                } ${saving ? "opacity-50 cursor-wait" : ""}`}
              >
                {BOOKING_STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==== Root Admin ====
const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const logout = async () => {
    try {
      if (token) {
        await axios.post(
          `${API}/admin/logout`,
          {},
          { headers: { "X-Admin-Token": token } }
        );
      }
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
